import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';

const TeacherSetup = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const libraryId = searchParams.get('libraryId');
    // useAuth hook to get current user
    const { user } = useAuth();

    const [patientContext, setPatientContext] = useState('');
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [streamedContent, setStreamedContent] = useState('');

    // Library Save State
    const [saveToLibrary, setSaveToLibrary] = useState(false);
    const [authorName, setAuthorName] = useState(user?.name || '');
    const [tags, setTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);

    useEffect(() => {
        // Fetch available tags
        api.getTags().then(res => setTags(res.data.tags)).catch(console.error);

        if (libraryId) {
            setLoading(true);
            api.getLibraryScenario(libraryId)
                .then(res => {
                    // Combine details and symptoms into the editor context properly?
                    // The previous code had patient details separate? 
                    // No, the previous code in TeacherSetup.js (Step 191) used `patientContext` as a single blob for `patient_details`.
                    // And passed empty string for symptoms.
                    // So we should just use `patient_details` from library.
                    setPatientContext(res.data.patient_details);

                    // Pre-select tags if available
                    if (res.data.tags) {
                        setSelectedTags(res.data.tags.map(t => t.id));
                    }
                })
                .catch(err => toast.error("Failed to load scenario"))
                .finally(() => setLoading(false));
        } else {
            // Random starter if NOT loading from library
            const RANDOM_SCENARIOS = [
                {
                    name: "Heart",
                    text: `<h3>Patient Demographics</h3><p><strong>Name:</strong> John Doe</p><p><strong>Age:</strong> 55</p><p><strong>Chief Complaint:</strong> Chest pain</p>`
                },
                {
                    name: "Respiratory",
                    text: `<h3>Patient Demographics</h3><p><strong>Name:</strong> Sarah Jenkins</p><p><strong>Age:</strong> 24</p><p><strong>Chief Complaint:</strong> Shortness of breath</p>`
                }
            ];
            const random = RANDOM_SCENARIOS[Math.floor(Math.random() * RANDOM_SCENARIOS.length)];
            setPatientContext(random.text);
        }
    }, [libraryId]);

    const handleGenerateProfile = async () => {
        setGenerating(true);
        setStreamedContent('');

        try {
            const res = await fetch(`${api.defaults.baseURL}/api/simulator/generate-profile`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ patient_details: patientContext, symptoms: "" })
            });

            if (!res.ok) throw new Error(res.statusText);

            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let accumulated = "";
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, { stream: true });
                buffer += chunk;
                const lines = buffer.split('\n\n');
                buffer = lines.pop();

                for (const line of lines) {
                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6));
                            if (data.text) {
                                accumulated += data.text;
                                setStreamedContent(accumulated);
                            }
                        } catch (e) {
                            console.warn(e);
                        }
                    }
                }
            }
            setPatientContext(accumulated);
        } catch (err) {
            console.error("Profile generation failed:", err);
            toast.error("Failed to generate profile");
        } finally {
            setGenerating(false);
        }
    };

    const handleStart = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // 1. If "Save to Library" is checked, create library item
            if (saveToLibrary) {
                await api.createLibraryScenario({
                    patient_details: patientContext,
                    symptoms: "", // We merge everything into patient_details for now based on previous file structure
                    author_name: authorName,
                    tag_ids: selectedTags
                });
                toast.success("Saved to library!");
            }

            // 2. Always create a NEW scenario for the session (Clone or Fresh)
            // This ensures we don't modify the library item if we edited it.
            const scenRes = await api.post('/api/simulator/scenario', {
                patient_details: patientContext,
                symptoms: ""
            });
            const scenarioId = scenRes.data.id;

            // 3. Create Session
            const sessRes = await api.post('/api/simulator/session', {
                scenario_id: scenarioId
            });
            const sessionId = sessRes.data.id;

            navigate(`/simulation/${sessionId}`);
        } catch (err) {
            console.error(err);
            toast.error("Failed to start simulation");
        } finally {
            setLoading(false);
        }
    };

    const toggleTag = (id) => {
        setSelectedTags(prev => prev.includes(id) ? prev.filter(t => t !== id) : [...prev, id]);
    };

    return (
        <div style={{ maxWidth: '900px', margin: '0 auto', padding: '20px' }}>
            <h1 style={{ marginBottom: '10px' }} className="text-primary">Teacher Setup</h1>
            <p style={{ marginBottom: '20px' }} className="text-muted">
                Define the patient persona. Use the AI to expand on your points.
            </p>

            <form onSubmit={handleStart} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                        <label style={{ fontWeight: 'bold', fontSize: '18px' }}>Patient Context</label>
                        <button
                            type="button"
                            onClick={handleGenerateProfile}
                            disabled={generating}
                            style={{ padding: '8px 16px', background: generating ? '#ccc' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                        >
                            {generating ? '✨ Generating...' : '✨ AI Expansion'}
                        </button>
                    </div>

                    <div className="card-bg" style={{ border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
                        {generating ? (
                            <div className="secondary-bg" style={{ minHeight: '400px', padding: '15px' }} dangerouslySetInnerHTML={{ __html: streamedContent }} />
                        ) : (
                            <ReactQuill theme="snow" value={patientContext} onChange={setPatientContext} style={{ height: '400px', marginBottom: '50px' }} />
                        )}
                    </div>
                </div>

                {/* Library Save Options */}
                <div className="secondary-bg" style={{ padding: '20px', borderRadius: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginBottom: '15px' }}>
                        <input
                            type="checkbox"
                            checked={saveToLibrary}
                            onChange={(e) => setSaveToLibrary(e.target.checked)}
                            style={{ width: '20px', height: '20px', marginRight: '10px' }}
                        />
                        <span style={{ fontSize: '16px', fontWeight: 'bold' }}>Save this scenario to the Library</span>
                    </label>

                    {saveToLibrary && (
                        <div style={{ marginLeft: '30px' }}>
                            <div style={{ marginBottom: '15px' }}>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Author Name</label>
                                <input
                                    type="text"
                                    value={authorName}
                                    onChange={(e) => setAuthorName(e.target.value)}
                                    placeholder="e.g. Dr. House"
                                    className="input-bg"
                                    style={{ padding: '8px', width: '100%', maxWidth: '300px', borderRadius: '4px' }}
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '5px' }}>Tags</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {tags.map(tag => (
                                        <button
                                            key={tag.id}
                                            type="button" // Prevent form submit
                                            onClick={() => toggleTag(tag.id)}
                                            className={selectedTags.includes(tag.id) ? '' : 'tag-default'}
                                            style={{
                                                padding: '5px 10px',
                                                borderRadius: '15px',
                                                border: '1px solid var(--border-color)',
                                                background: selectedTags.includes(tag.id) ? '#0070f3' : undefined,
                                                color: selectedTags.includes(tag.id) ? 'white' : undefined,
                                                cursor: 'pointer',
                                                fontSize: '12px'
                                            }}
                                        >
                                            {tag.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button
                        type="submit"
                        disabled={loading || generating}
                        style={{ padding: '15px 30px', fontSize: '18px', background: '#0070f3', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', opacity: (loading || generating) ? 0.7 : 1 }}
                    >
                        {loading ? 'Initializing...' : 'Start Session'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default TeacherSetup;
